import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaLock, FaEye, FaEyeSlash } from 'react-icons/fa';
import { supabase } from '../supabaseClient';
import { LoadingButton } from '../components/LoadingSpinner';
import './login.css';

function ResetPassword() {
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [sessionReady, setSessionReady] = useState(false);
    const [sessionError, setSessionError] = useState('');
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        const hash = window.location.hash.substring(1);
        const params = new URLSearchParams(hash);
        const access_token = params.get('access_token');
        const refresh_token = params.get('refresh_token');
        const type = params.get('type');

        if (type === 'recovery' && access_token && refresh_token) {
            supabase.auth.setSession({ access_token, refresh_token }).then(({ error }) => {
                if (error) {
                    setSessionError('Invalid or expired reset link. Please request a new one.');
                } else {
                    setSessionReady(true);
                }
            });
        } else {
            setSessionError('Invalid reset link. Please request a new one.');
        }
    }, []);

    const handleSubmit = async (event) => {
        event.preventDefault();
        setError('');

        if (password !== confirmPassword) {
            setError('Passwords do not match.');
            return;
        }
        if (password.length < 8) {
            setError('Password must be at least 8 characters.');
            return;
        }

        setIsLoading(true);
        try {
            const { error } = await supabase.auth.updateUser({ password });
            if (error) {
                setError(error.message);
            } else {
                setSuccess(true);
                setTimeout(() => navigate('/'), 3000);
            }
        } catch (err) {
            setError('Something went wrong. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="login-container">
            <div className="login-content">
                <div className="login-card">
                    <div className="login-header">
                        <div className="logo-container">
                            <img
                                src="/favicon.ico"
                                alt="Wandawazi Flow Logo"
                                className="login-logo"
                                style={{ width: '120px', height: 'auto', maxHeight: '50px', objectFit: 'contain', filter: 'none' }}
                            />
                        </div>
                        <h1 className="login-title">Set New Password</h1>
                        <p className="login-subtitle">Choose a strong password for your account</p>
                    </div>

                    <form onSubmit={handleSubmit} className="login-form">
                        {sessionError && (
                            <div className="error-message" style={{ margin: '0 0 1rem' }}>{sessionError}</div>
                        )}

                        {success && (
                            <div className="reset-success-message">
                                Password updated successfully! Redirecting you to sign in...
                            </div>
                        )}

                        {!sessionError && !success && (
                            <>
                                {error && <div className="error-message" style={{ margin: '0 0 1rem' }}>{error}</div>}

                                <div className="form-group">
                                    <label htmlFor="password" className="form-label">
                                        <FaLock className="input-icon" />
                                        New Password
                                    </label>
                                    <div className="password-input-container">
                                        <input
                                            type={showPassword ? 'text' : 'password'}
                                            id="password"
                                            className="form-input"
                                            placeholder="Enter new password"
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            required
                                            disabled={!sessionReady}
                                        />
                                        <button type="button" className="password-toggle" onClick={() => setShowPassword(!showPassword)}>
                                            {showPassword ? <FaEyeSlash /> : <FaEye />}
                                        </button>
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label htmlFor="confirmPassword" className="form-label">
                                        <FaLock className="input-icon" />
                                        Confirm Password
                                    </label>
                                    <div className="password-input-container">
                                        <input
                                            type={showConfirm ? 'text' : 'password'}
                                            id="confirmPassword"
                                            className="form-input"
                                            placeholder="Confirm new password"
                                            value={confirmPassword}
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                            required
                                            disabled={!sessionReady}
                                        />
                                        <button type="button" className="password-toggle" onClick={() => setShowConfirm(!showConfirm)}>
                                            {showConfirm ? <FaEyeSlash /> : <FaEye />}
                                        </button>
                                    </div>
                                </div>

                                <LoadingButton
                                    type="submit"
                                    className="login-btn"
                                    isLoading={isLoading}
                                    loadingText="Updating..."
                                    variant="primary"
                                    disabled={!sessionReady}
                                >
                                    Update Password
                                </LoadingButton>
                            </>
                        )}
                    </form>
                </div>
            </div>
        </div>
    );
}

export default ResetPassword;
